import { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from './card';
import { Eye, Loader2 } from 'lucide-react';

interface FileViewerProps {
  file: File | null;
  fileUrl?: string;
  apiKey?: string;
  className?: string;
}

export function FileViewer({ file, fileUrl, apiKey = 'demo-api-key', className }: FileViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!file && !fileUrl) return;

    // Load Nutrient Web SDK script if not already loaded
    const loadNutrientSDK = () => {
      return new Promise<void>((resolve, reject) => {
        if (window.NutrientViewer) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://cdn.cloud.pspdfkit.com/pspdfkit-web@1.10.0/nutrient-viewer.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Nutrient Web SDK'));
        document.head.appendChild(script);
      });
    };

    const initializeViewer = async () => {
      if (!containerRef.current) return;

      setLoading(true);

      try {
        await loadNutrientSDK();

        // Clean up previous instance using the container
        if (window.NutrientViewer && containerRef.current) {
          try {
            window.NutrientViewer.unload(containerRef.current);
          } catch (e) {
            // Ignore errors if no instance was loaded
            console.debug('No previous instance to unload');
          }
        }

        // Clear the container content
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }

        let documentSource;
        
        if (file) {
          // Handle File object - use ArrayBuffer directly
          documentSource = await file.arrayBuffer();
        } else if (fileUrl) {
          // Handle URL
          documentSource = fileUrl;
        } else {
          throw new Error('No file or URL provided');
        }

        // Initialize Nutrient Viewer
        if (!window.NutrientViewer) {
          throw new Error('Nutrient Web SDK not loaded');
        }
        await window.NutrientViewer.load({
          container: containerRef.current,
          document: documentSource,
          // Add file type information for Office files
          ...(file && {
            filename: file.name,
            contentType: file.type
          }),
          apiKey: apiKey,
          toolbar: {
            default: ['zoom-in', 'zoom-out', 'page-navigation', 'download', 'print'],
            disabled: ['annotation']
          }
        });
      } catch (err) {
        console.error('Error initializing viewer:', err);
      } finally {
        setLoading(false);
      }
    };

    initializeViewer();

    return () => {
      if (window.NutrientViewer && containerRef.current) {
        try {
          window.NutrientViewer.unload(containerRef.current);
        } catch (e) {
          // Ignore errors if no instance was loaded
          console.debug('No instance to unload during cleanup');
        }
      }
    };
  }, [file, fileUrl, apiKey]);

  if (!file && !fileUrl) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center h-96">
          <div className="text-center text-gray-500">
            <Eye className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No file selected for preview</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
      <div className="p-0">
        {/* {error && (
          <div className="m-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )} */}
        
        {loading && (
          <div className="flex items-center justify-center h-96">
            <div className="flex flex-col items-center space-y-2">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <p className="text-sm text-gray-600">Loading document...</p>
            </div>
          </div>
        )}
        
        <div
          ref={containerRef}
          className="w-full"
          style={{ height: '600px', display: loading ? 'none' : 'block' }}
        />
      </div>
  );
}

// Extend the Window interface to include NutrientViewer
declare global {
  interface Window {
    NutrientViewer?: {
      load: (options: {
        container: HTMLElement;
        document: string | ArrayBuffer;
        apiKey?: string;
        theme?: string;
        filename?: string;
        contentType?: string;
        toolbar?: {
          default?: string[];
          disabled?: string[];
        };
      }) => Promise<any>;
      unload: (instanceOrContainer: any | HTMLElement) => void;
    };
  }
}

export default FileViewer;